import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LeadDetailsResponse, LeadNotePayload, UpdateLeadNotePayload } from '../types';
import { LEAD_DETAILS } from './constants';

// HWM-22633 round 4: server-confirmed cache patch on success. The PATCH
// response is the new canonical `latest_note` shape — apply it to
// [LEAD_DETAILS, leadId] directly so the detail-view StickyNotePanel updates
// without a refetch.
const usePatchLeadNote = (onSuccess?: (data: LeadNotePayload) => void) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({
        leadId,
        noteId,
        payload,
        silent = false
    }: {
        leadId: string;
        noteId: number;
        payload: UpdateLeadNotePayload;
        silent?: boolean;
    }): Promise<LeadNotePayload & { __silent?: boolean }> => {
        return req({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/leads/${leadId}/notes/${noteId}/`,
            method: 'PATCH',
            body: JSON.stringify(payload)
        }).then((res) => {
            (res as { __silent?: boolean }).__silent = silent;
            return res as LeadNotePayload & { __silent?: boolean };
        });
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (data, vars) => {
            queryClient.setQueryData<LeadDetailsResponse | undefined>(
                [LEAD_DETAILS, vars.leadId],
                (old) =>
                    old
                        ? {
                              ...old,
                              latest_note: {
                                  id: data.id,
                                  body: data.body,
                                  updated_at: data.updated_at
                              }
                          }
                        : old
            );
            if (!data.__silent) {
                setNotification({ message: 'Note updated' });
            }
            onSuccess && onSuccess({ id: data.id, body: data.body, updated_at: data.updated_at });
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default usePatchLeadNote;
